import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

type WakeLockType = 'screen';

interface WakeLockSentinelLike extends EventTarget {
  released: boolean;
  release(): Promise<void>;
}

interface WakeLockManagerLike {
  request(type: WakeLockType): Promise<WakeLockSentinelLike>;
}

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: WakeLockManagerLike;
};

@Injectable({
  providedIn: 'root'
})
export class ScreenWakeLockService {
  private wakeLockSentinel?: WakeLockSentinelLike;
  private wakeLockRequest?: Promise<void>;
  private initialized = false;

  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.document.addEventListener('click', this.handleUserInteraction, true);
    this.document.addEventListener('touchstart', this.handleUserInteraction, true);
    this.document.addEventListener('keydown', this.handleUserInteraction, true);
    this.document.defaultView?.addEventListener('focus', this.handleUserInteraction);

    void this.requestWakeLock();
  }

  private readonly handleVisibilityChange = (): void => {
    if (this.document.hidden) {
      return;
    }

    void this.requestWakeLock();
  };

  private readonly handleUserInteraction = (): void => {
    void this.requestWakeLock();
  };

  private readonly handleWakeLockRelease = (): void => {
    this.wakeLockSentinel?.removeEventListener('release', this.handleWakeLockRelease);
    this.wakeLockSentinel = undefined;
  };

  private async requestWakeLock(): Promise<void> {
    if (this.wakeLockSentinel?.released) {
      this.wakeLockSentinel.removeEventListener('release', this.handleWakeLockRelease);
      this.wakeLockSentinel = undefined;
    }

    if (this.document.hidden || this.wakeLockSentinel || this.wakeLockRequest) {
      return;
    }

    const wakeLockManager = this.getWakeLockManager();

    if (!wakeLockManager) {
      return;
    }

    this.wakeLockRequest = wakeLockManager
      .request('screen')
      .then((wakeLockSentinel) => {
        this.wakeLockSentinel = wakeLockSentinel;
        this.wakeLockSentinel.addEventListener('release', this.handleWakeLockRelease);
      })
      .catch((error: unknown) => {
        console.warn('Unable to activate screen wake lock.', error);
      })
      .finally(() => {
        this.wakeLockRequest = undefined;
      });

    await this.wakeLockRequest;
  }

  private getWakeLockManager(): WakeLockManagerLike | undefined {
    const navigator = this.document.defaultView?.navigator as NavigatorWithWakeLock | undefined;
    return navigator?.wakeLock;
  }
}
